import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/index'
import { SearchRobViewStore } from '@planning/rt-stores/tallyman/SearchRobViewStore'
import { useTranslate } from '@tolgee/react'
import { ContainerMobile, Header, HighlightedText, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect } from 'react'
import { CustomAlert } from './Components/CustomAlert'
import { NavigationStackBackButton } from './Components/HeaderComponents/NavigationStackBackButton'
import { useTallymanMessageHandler } from './Hooks/useTallymanMessageHandler'

interface IProps {
  store: SearchRobViewStore
  onContainerCardClick: (id?: number) => void
  operationStoppedComponent?: React.ReactNode
}

export const SearchRob: FC<IProps> = observer(
  ({ store, onContainerCardClick, operationStoppedComponent }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { appViewStore } = usePlanningStore()
    const { getCreateRestowFromRemainOnBoardExceptionMessage } = useTallymanMessageHandler()

    useEffect(() => {
      window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
      store.setSearchFilter(store.parentStore.searchContainerStore.searchFilter)
    }, [store, store.parentStore.searchContainerStore.searchFilter])

    const handleCreateRestowFromROB = (robId: number) => {
      return async () => {
        if (store.parentStore.isSelectedVisitInOperation) {
          try {
            store.setLoading(true)
            const id = await store.createRestowFromRob(robId)
            store.removeRobUnitFromListById(robId)
            onContainerCardClick(id)
          } catch (error) {
            appViewStore.setShowAlert(
              'error',
              getCreateRestowFromRemainOnBoardExceptionMessage(error),
            )
          } finally {
            store.setLoading(false)
          }
        }
      }
    }

    return (
      <>
        <Header
          filterTestId='search-rob-autocomplete'
          leftRenderOption={<NavigationStackBackButton />}
          enableSearchField
          onSearchFieldChange={store.setSearchFilter}
          searchFilter={store.searchFilter}
        />

        <Box
          style={{
            backgroundColor: theme.palette.grey[200],
            minHeight: 'calc(100vh - 65px)',
          }}
        >
          <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
            <Stack gap={theme.customSpacing.m}>
              {!store.isSearchFilterValid && (
                <CustomAlert
                  message={t(
                    'hintTryToTypeAtLeast5CharactersOfTheContainerNumber',
                    'Hint, try to type at least 5 characters of the container number',
                  )}
                  severity='info'
                />
              )}
              {store.isSearchFilterValid &&
                store.robUnits?.map(o => (
                  <>
                    <Card
                      sx={{
                        mb: '1rem',
                      }}
                      onClick={handleCreateRestowFromROB(o.id)}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant='h3'>
                            <HighlightedText highlight={store.searchFilter}>
                              {o.number}
                            </HighlightedText>
                          </Typography>
                        </Box>
                        <Button
                          data-cy='create-new-restow'
                          variant='contained'
                          size='large'
                          sx={{ width: '100%', mt: '1rem' }}
                          disabled={!store.parentStore.isSelectedVisitInOperation || store.loading}
                        >
                          {t('createRestow', 'Create restow')}
                        </Button>
                      </CardContent>
                    </Card>
                  </>
                ))}

              {store.isSearchFilterValid && !store.robUnits?.length && !store.loading && (
                <>
                  <CustomAlert
                    severity='warning'
                    message={t(
                      'containerNotInSystem',
                      'Container is not in the system. You can add it for further investigation.',
                    )}
                  ></CustomAlert>
                  <Button
                    data-cy='add-overlanded-container'
                    variant='contained'
                    size='large'
                    sx={{ width: '100%', mt: '1rem' }}
                    onClick={() => onContainerCardClick()}
                  >
                    {t('add', 'Add')}
                  </Button>
                </>
              )}
              {operationStoppedComponent}
            </Stack>
          </ContainerMobile>
        </Box>
      </>
    )
  },
)
